import _ from 'lodash';
import { Flex, Spin, theme } from 'antd';
import { formatCentsToDollar } from '../../../assets/currencies';
import CalendarIcon from '../../../assets/icons/calendar-for-cost-estimate-icon.svg';
import PuzzleIcon from '../../../assets/icons/puzzle-for-cost-estimate.svg';
import CreditCardIcon from '../../../assets/icons/credit-card-icon.svg';
import DollarIcon from '../../../assets/icons/dollar-icon.svg';
import TagIcon from '../../../assets/icons/billing-done-tag-icon.svg';
import { useGetCostEstimateQuery, useGetInvoicesQuery } from '../api/billingApiSlice';
import { useTrial } from '~features/FreeTrial';
import { Typography } from 'antd';
import { useIsSubscriptionCancelled } from '../../../hooks/usePrerenderUser';

function TrialCostEstimate() {
  return (
    <div>
      <Typography.Paragraph strong>
        You are currently on a free trial of the Starter plan, no extra costs are applied.
      </Typography.Paragraph>
    </div>
  );
}

function CancelledCostEstimate() {
  return (
    <div>
      <Typography.Paragraph strong>
        Your subscription has been cancelled and rendering has stopped, no extra costs are applied.
      </Typography.Paragraph>
    </div>
  );
}

function NormalCostEstimate() {
  const { token } = theme.useToken();
  const costRow = ({ icon, label, value, style }) => (
    <Flex component="li" align="center" justify="space-between" style={style}>
      <Flex align="center" gap={token.marginSM}>
        <img src={icon} />
        <span>{label}</span>
      </Flex>
      <span>{value}</span>
    </Flex>
  );
  const {
    data: costEstimate,
    isError: isErrorLoadingCostEstimate,
    isLoading: isLoadingCostEstimate,
  } = useGetCostEstimateQuery();
  const { data: invoicesList } = useGetInvoicesQuery();

  if (isErrorLoadingCostEstimate) {
    return (
      <p style={{ color: 'red' }}>
        An unexpected error has occured while retrieving your cost estimate. Please try again by reloading the page.
      </p>
    );
  }

  if (isLoadingCostEstimate) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    );
  }

  const latestPlanInvoice = invoicesList?.data?.find((invoice) => invoice.isPackageOnly === false);
  const hasChargedSubscriptionCost =
    _.isNumber(costEstimate.data?.reservedCost) &&
    _.isNumber(latestPlanInvoice?.amountPaidInCent) &&
    latestPlanInvoice.amountPaidInCent !== costEstimate.data.reservedCost;

  const hasPackagesCost =
    costEstimate.data && _.isNumber(costEstimate.data.packagesCost) && costEstimate.data.packagesCost > 0;

  const hasSubscriptionDiscount =
    _.isNumber(costEstimate.data?.subscriptionDiscount) && costEstimate.data.subscriptionDiscount > 0;
  return (
    <ul style={{ marginTop: token.marginLG, paddingLeft: 0, listStyle: 'none' }}>
      {costRow({
        icon: CalendarIcon,
        label: 'Subscription Cost',
        value: !_.isNumber(costEstimate.data?.reservedCost)
          ? 'n/a'
          : `${formatCentsToDollar(costEstimate.data.reservedCost || 0)}${
              hasChargedSubscriptionCost && !hasSubscriptionDiscount
                ? ` (charged ${formatCentsToDollar(latestPlanInvoice.amountPaidInCent)})`
                : ''
            }`,
      })}
      <hr />
      {hasSubscriptionDiscount && (
        <>
          {costRow({
            icon: TagIcon,
            label: costEstimate.data.subscriptionDiscountLabel
              ? `Discount (${costEstimate.data.subscriptionDiscountLabel})`
              : 'Discount',
            value: `-${formatCentsToDollar(costEstimate.data.subscriptionDiscount)}`,
          })}
          <hr />
        </>
      )}
      {hasPackagesCost && (
        <>
          {costRow({
            icon: PuzzleIcon,
            label: 'Packages Cost',
            value: formatCentsToDollar(costEstimate.data.packagesCost),
          })}
          <hr />
        </>
      )}
      {costRow({
        icon: CreditCardIcon,
        label: 'Extra Renders Cost',
        value: !_.isNumber(costEstimate.data?.onDemandCost)
          ? 'n/a'
          : formatCentsToDollar(costEstimate.data.onDemandCost || 0),
      })}
      <hr />
      {costRow({
        icon: DollarIcon,
        label: 'Total Estimated Cost',
        style: { fontWeight: token.fontWeightStrong },
        value:
          _.isNumber(costEstimate.data?.reservedCost) ||
          _.isNumber(costEstimate.data?.packagesCost) ||
          _.isNumber(costEstimate.data?.onDemandCost)
            ? formatCentsToDollar(
                (costEstimate?.data?.reservedCost || 0) -
                  (costEstimate?.data?.subscriptionDiscount || 0) +
                  (costEstimate?.data?.packagesCost || 0) +
                  (costEstimate?.data?.onDemandCost || 0)
              )
            : 'n/a',
      })}
    </ul>
  );
}

function CorrectCostEstimate() {
  const { isInTrial } = useTrial();
  const isSubscriptionCancelled = useIsSubscriptionCancelled();

  if (isSubscriptionCancelled) return <CancelledCostEstimate />;
  if (isInTrial) return <TrialCostEstimate />;
  return <NormalCostEstimate />;
}

export const BillingOverviewCostEstimate = () => {
  const { token } = theme.useToken();
  return (
    <div>
      <h3 style={{ fontWeight: token.fontWeightStrong }}>Cost Estimate</h3>
      <p>This is your cost estimate based on usage over the last 30 days.</p>
      <CorrectCostEstimate />
    </div>
  );
};
